import { useEffect } from 'react';
import type React from 'react';

import { Image, Text } from '@change/ds-core-ui';
import { TextArea, TextField } from '@change/ds-forms';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import supporterVoicesComments from '#src/app/pages/actions/shared/assets/supporter-voices-comments.svg';
import { useCurrentUser } from '#src/app/shared/hooks/session';

import { ActionCard } from '../ActionCard';

import { SupporterVoicesButton } from './components/SupporterVoicesButton';
import { useShareSupporterVoice } from './hooks/useShareSupporterVoice';

export const AddSupporterVoice: React.FC = () => {
	const currentUser = useCurrentUser();
	const {
		data: { voiceValue: text, addVoiceInProgress },
		actions: { setVoiceValue, shareVoice },
	} = useShareSupporterVoice();
	const [{ shareSupporterVoiceError }, { setShareSupporterVoiceError }] = usePetitionActionsPageState();
	const { translate } = useI18n();

	const track = useTracking();

	useEffect(() => {
		void track('action_page_view', {
			action_type: 'voice_text',
			login_prompt: !currentUser,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUser]);

	return (
		<ActionCard data-testid="supporter-voice-box" className="shadow-[0_10px_10px_0_rgba(0,0,0,0.1)]">
			<div className="mb-4 text-center">
				<div className="mt-2 mb-4 flex h-12 justify-center">
					<Image src={supporterVoicesComments} alt="add-supporter-voice-icon" className="h-full w-auto" />
				</div>
				<Text as="h2" variant="heading" size="large" fontWeight="bold" className="text-typography-strong mb-4">
					{translate('corgi.petition_actions.supporter_voice_text.heading')}
				</Text>
				<Text as="p" variant="body" size="medium" className="text-typography-strong mb-4">
					{translate('corgi.petition_actions.supporter_voice_text.call_to_action')}
				</Text>
				<div className="mx-auto w-full md:w-1/2">
					<TextField
						input={<TextArea />}
						label={translate('corgi.petition_actions.supporter_voice_text.text_input.label')}
						labelIsVisible={false}
						name="petition-add-supporter-voice"
						onChange={(value) => {
							setShareSupporterVoiceError(undefined);
							setVoiceValue(value);
						}}
						value={text}
						placeholder={translate('corgi.petition_actions.supporter_voice_text.text_input.label')}
						errorMessage={shareSupporterVoiceError}
						isInvalid={!!shareSupporterVoiceError}
						isDisabled={!currentUser}
						data-qa="supporter-voice-text-input"
					/>
				</div>
			</div>
			<div className="text-center">
				<SupporterVoicesButton text={text} addVoiceInProgress={addVoiceInProgress} shareVoice={shareVoice} />
			</div>
		</ActionCard>
	);
};
